import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  map,
  merge,
  share,
  startWith,
  Subscription,
  switchMap,
  takeUntil,
  Observable,
  BehaviorSubject,
  finalize,
  observeOn,
  asapScheduler,
  combineLatest,
} from 'rxjs';

import EsriMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { MovingUtils } from 'app/shared/utils/moving.utils';

@Component({
  selector: 'app-overview-map',
  templateUrl: 'overview-map.component.html',
  styleUrls: ['overview-map.component.scss'],
})
export class OverviewMapComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('mapViewEl', { static: true })
  mapViewElRef: ElementRef<HTMLDivElement>;

  @ViewChild('handleEl', { static: true })
  handleElRef: ElementRef<HTMLDivElement>;

  @Input()
  mapView: __esri.MapView;

  private mapInternal: __esri.Map;
  private mapViewInternal: __esri.MapView;

  private targetHandleMapX: number = 0;
  private targetHandleMapY: number = 0;
  private handleX: number = 0;
  private handleY: number = 0;
  private handleWidth: number = 0;
  private handleHeight: number = 0;

  private syncHandlePosWithMapViewSubject = new BehaviorSubject<boolean>(true);

  private mapViewSubscription: Subscription;

  ngOnInit() {
    this.mapInternal = new EsriMap();
    this.mapViewInternal = new MapView({
      container: this.mapViewElRef.nativeElement,
      map: this.mapInternal,
      constraints: {
        rotationEnabled: false,
        snapToZoom: false,
      },
    });
    this.mapViewInternal.ui.components = [];

    this.handleMapView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView && !changes.mapView.firstChange) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.mapViewSubscription?.unsubscribe();

    this.destroyBasemap(this.mapInternal.basemap);

    this.mapViewInternal?.destroy();
    this.mapInternal?.destroy();
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    subscription.add(
      EsriUtils.watch(this.mapView.map, 'basemap')
        .pipe(
          map(() => this.mapView.map.basemap),
          startWith(this.mapView.map.basemap),
          distinctUntilChanged()
        )
        .subscribe((basemap) => {
          const lastBasemap = this.mapInternal.basemap;
          this.mapInternal.basemap = basemap?.clone();
          this.destroyBasemap(lastBasemap);
        })
    );

    subscription.add(
      EsriUtils.watch(this.mapView, 'extent')
        .pipe(startWith(null))
        .subscribe(() => {
          this.updateHandleDimensions();
        })
    );

    subscription.add(
      merge(
        this.syncHandlePosWithMapViewSubject.pipe(
          switchMap((active) => {
            if (!active) {
              return EMPTY;
            }

            return EsriUtils.watch(this.mapView, 'extent').pipe(
              startWith(this.mapView.extent)
            );
          })
        ),
        EsriUtils.watch(this.mapViewInternal, 'extent')
      )
        .pipe(observeOn(asapScheduler))
        .subscribe(() => {
          this.updateHandlePos();
        })
    );

    subscription.add(this.setupMapViewpointEvents().subscribe());
    subscription.add(this.setupHandleMovingEvents().subscribe());

    this.mapViewSubscription = subscription;
  }

  private setupMapViewpointEvents() {
    const animationObservable = EsriUtils.watch(this.mapView, 'animation').pipe(
      share()
    );

    const navigatingObservable = EsriUtils.watch(
      this.mapView,
      'navigating'
    ).pipe(share());

    return merge(
      EsriUtils.watch(this.mapView, 'viewpoint').pipe(first()),
      navigatingObservable.pipe(
        filter((x) => x.newValue),
        switchMap(() => {
          return navigatingObservable.pipe(
            first((x) => !x.newValue),
            takeUntil(animationObservable.pipe(first()))
          );
        })
      ),
      animationObservable.pipe(filter((x) => !x.newValue && x.oldValue?.done))
    ).pipe(
      map(() => this.mapView.viewpoint),
      startWith(this.mapView.viewpoint),
      distinctUntilChanged((lastViewpoint, currentViewpoint) => {
        return EsriUtils.isViewpointEquals(lastViewpoint, currentViewpoint);
      }),
      debounceTime(100),
      filter((x) => !!x),
      switchMap(() => {
        const scale = this.getNewScale();
        return EsriUtils.goTo(this.mapViewInternal, {
          center: this.mapView.center,
          scale,
        });
      })
    );
  }

  private setupHandleMovingEvents() {
    return new Observable((subscriber) => {
      let handleInitialX: number;
      let handleInitialY: number;
      let goToByMovingHandleSubscription: Subscription;
      subscriber.add(() => {
        goToByMovingHandleSubscription?.unsubscribe();
      });

      subscriber.add(
        MovingUtils.observeMovingEvents({
          elementToStartEvent: this.handleElRef.nativeElement,
          onMoveStart: () => {
            handleInitialX = this.handleX;
            handleInitialY = this.handleY;
          },
          onMoveEnd: () => {
            const handleInitialMapPoint = this.mapViewInternal.toMap({
              x: handleInitialX,
              y: handleInitialY,
            });
            const handleMapPoint = this.mapViewInternal.toMap({
              x: this.handleX,
              y: this.handleY,
            });

            const center = this.mapViewInternal.center.clone();
            center.x -= handleInitialMapPoint.x - handleMapPoint.x;
            center.y -= handleInitialMapPoint.y - handleMapPoint.y;

            this.targetHandleMapX = center.x;
            this.targetHandleMapY = center.y;

            goToByMovingHandleSubscription?.unsubscribe();

            this.syncHandlePosWithMapViewSubject.next(false);
            goToByMovingHandleSubscription = combineLatest(
              EsriUtils.goTo(this.mapView, {
                center,
                scale: this.mapView.scale,
              }),
              EsriUtils.goTo(this.mapViewInternal, {
                center,
                scale: this.getNewScale(),
              })
            )
              .pipe(
                finalize(() => {
                  this.syncHandlePosWithMapViewSubject.next(true);
                })
              )
              .subscribe();
          },
        }).subscribe((e) => {
          let x = handleInitialX + e.stepPoint.x;
          let y = handleInitialY + e.stepPoint.y;

          this.setHandlePos(x, y);
        })
      );
    });
  }

  private updateHandlePos() {
    const mapViewSizeWidthHalf = this.mapViewInternal.width / 2;
    const mapViewSizeHeightHalf = this.mapViewInternal.height / 2;

    const syncModeEnabled = this.syncHandlePosWithMapViewSubject.getValue();

    const center = this.mapViewInternal.toScreen(
      !syncModeEnabled
        ? ({ x: this.targetHandleMapX, y: this.targetHandleMapY } as any)
        : EsriUtils.project(
            (this.mapView.extent?.center ?? this.mapView.center)?.clone(),
            this.mapViewInternal.spatialReference
          )
    );

    const centerScreenPointInternal = this.mapViewInternal.toScreen(
      (
        this.mapViewInternal.extent?.center ?? this.mapViewInternal.center
      )?.clone()
    );
    if (centerScreenPointInternal) {
      center.x -= centerScreenPointInternal.x - mapViewSizeWidthHalf;
      center.y -= centerScreenPointInternal.y - mapViewSizeHeightHalf;
    }

    let x = center?.x ?? mapViewSizeWidthHalf;
    let y = center?.y ?? mapViewSizeHeightHalf;

    this.setHandlePos(x, y);
  }

  private setHandlePos(x: number, y: number) {
    if (this.handleX !== x) {
      this.handleX = x;
      this.handleElRef.nativeElement.style.setProperty('left', x + 'px');
    }

    if (this.handleY !== y) {
      this.handleY = y;
      this.handleElRef.nativeElement.style.setProperty('top', y + 'px');
    }
  }

  private updateHandleDimensions() {
    const newScale = this.getNewScale();
    const width = this.adaptToInternalScale(this.mapView.width, newScale);
    const height = (this.mapView.height / this.mapView.width) * width;

    if (this.handleWidth !== width) {
      this.handleWidth = width;
      this.handleElRef.nativeElement.style.setProperty('width', width + 'px');
    }

    if (this.handleHeight !== height) {
      this.handleHeight = height;
      this.handleElRef.nativeElement.style.setProperty('height', height + 'px');
    }
  }

  private adaptToInternalScale(value: number, scale: number): number {
    return (value * this.mapView.scale) / scale;
  }

  private getNewScale() {
    return (
      this.mapView.scale *
      2 *
      Math.max(
        this.mapView.width / this.mapViewInternal.width,
        this.mapView.height / this.mapViewInternal.height
      )
    );
  }

  private destroyBasemap(basemap: __esri.Basemap) {
    if (!basemap) {
      return;
    }

    basemap.baseLayers.removeAll();
    basemap.referenceLayers.removeAll();
    basemap.portalItem = null;

    basemap.destroy();
  }
}
